import { Fragment, memo, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/shared/components/ui/breadcrumb';
import { useBreadcrumbs } from 'src/shared/hooks/useBreadcrumbs';

type PageBreadcrumbsProps = {
  className?: string;
  fallbackBreadcrumb?: {
    label: string;
    href?: string;
    component?: ReactNode;
  };
};

function PageBreadcrumbs({ className, fallbackBreadcrumb }: PageBreadcrumbsProps) {
  const { breadcrumbs } = useBreadcrumbs();

  const shouldUseFallback =
    fallbackBreadcrumb && (breadcrumbs.length === 0 || breadcrumbs[0]?.label !== fallbackBreadcrumb.label);

  const items = shouldUseFallback ? [fallbackBreadcrumb] : breadcrumbs;

  if (items.length === 0) {
    return null;
  }

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {items.map((breadcrumb, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={`${breadcrumb.label}-${index}`}>
              <BreadcrumbItem className="font-semibold">
                {breadcrumb.component ? (
                  breadcrumb.component
                ) : isLast ? (
                  <BreadcrumbPage className="text-typography-black">{breadcrumb.label}</BreadcrumbPage>
                ) : breadcrumb.href ? (
                  <BreadcrumbLink asChild>
                    <Link to={breadcrumb.href}>{breadcrumb.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <span className="text-muted-foreground">{breadcrumb.label}</span>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export default memo(PageBreadcrumbs);
